/**
 * Auth payload types — kept structurally identical to the
 * process-runner `LoginRequest` / `LoginResponse` shapes so the same
 * backend endpoints work without translation.
 */

export const AppType = {
  indirect: 'indirect',
} as const
export type AppTypeValue = (typeof AppType)[keyof typeof AppType]

export interface LoginRequest {
  userName?: string
  password?: string
  sessionId?: string
  tenantId?: string
  code?: string
  clientId?: string
  uri?: string
  respondingTo?: string
  smsCode?: string
  authType?: string
  appType: AppTypeValue
}

export interface LoginErrorPayload {
  loginStatus?: string
  message?: string
  exceptionMessage?: string
  sessionId?: string
  status?: string
  socialSigninError?: { message?: string; exceptionMessage?: string }
}

export interface LoginResponse {
  // Status / challenge bits
  loginStatus?: string
  status?: string
  sessionId?: string
  conversationId?: string
  secretCode?: string
  redirectTo?: string
  // Tokens / user
  idToken?: string
  accessToken?: string
  refreshToken?: string
  username?: string
  expiryTime?: number
  attributes?: { tenantId?: string; conversationId?: string; [k: string]: unknown }
  activeSessions?: Array<{ id: string; name: string }>
  user?: Record<string, unknown>
  picture?: string
  appAdmin?: boolean
  buyerAdmin?: boolean
  anyAdmin?: boolean
  logoutUrl?: string
  urls?: Array<{ name: string; id?: string }>
  // Wire-level wrappers (some endpoints nest the response in `.data`;
  // others return it at top level — handle both).
  data?: LoginResponse
  // Carried by the axios interceptor when the response is 4xx.
  error?: LoginErrorPayload
}

/** `POST /auth/refreshToken` success body. */
export interface RefreshResponse {
  idToken?: string
  accessToken?: string
  refreshToken?: string
  expiryTime?: number
  username?: string
  attributes?: { tenantId?: string; [k: string]: unknown }
}

export interface LoginSettingResponse {
  authType: string
  clientId: string
  connectionName: string
  authUrl: string
  redirectUrl?: string
  pkce?: { codeChallenge: string; codeChallengeMethod: string }
}

export interface OtpMethodInfo {
  mobile?: string
  email?: string
  fromName?: string
  fromEmail?: string
  redirectUrl?: string
}

export interface OtpVerifyResponse {
  targetUrl?: string
  contact?: { name?: string; email?: string; phone?: string }
  attributes?: { tenantId?: string }
  expiryTime?: number
  email?: string
}

export interface ForgotPasswordRequest {
  userName: string
  password?: string
  tenantId?: string
  verificationCode?: string
}

/**
 * Backend `LoginStatus` enum values — snake_case strings emitted by
 * `auth-service/AuthController`. Frontend branches on these to drive
 * the multi-step auth flow (confirm SMS, change password, MFA setup,
 * reset password, etc.).
 */
export const LoginErrorEnum = {
  successful: 'successful',
  ambiguousUsername: 'ambiguous_username',
  invalidUsernamePassword: 'invalid_username_password',
  lockedOut: 'locked_out',
  newPasswordRequired: 'new_password_required',
  invalidSessionId: 'invalid_session_id',
  invalidCodeOrAuthState: 'invalid_code_or_auth_state',
  expiredSessionId: 'expired_session_id',
  nonCognitoUser: 'non_cognito_user',
  nonAdminUser: 'non_admin_user',
  ineligibleForImpersonation: 'ineligible_for_impersonation',
  smsCodeRequired: 'sms_code_required',
  mfaSetup: 'mfa_setup',
  mfaSetupVerify: 'mfa_setup_verify',
  selectMfaType: 'select_mfa_type',
  softwareTokenMfa: 'software_token_mfa',
  loginAgain: 'login_again',
  supplierPortalNotEnabled: 'supplier_portal_not_enabled',
  invalidRequest: 'invalid_request',
  supplierPortalAccessRestricted: 'supplier_portal_access_restricted',
  passwordResetRequired: 'password_reset_required',
  passwordRequired: 'password_required',
  userNotProvisioned: 'user_not_provisioned',
  refreshTokenLocked: 'refreshTokenLocked',
  default: 'default',
} as const
export type LoginErrorEnumValue =
  (typeof LoginErrorEnum)[keyof typeof LoginErrorEnum]

/**
 * Maps a non-success `loginStatus` to the route to navigate to. Mirrors
 * process-runner's `getBasePathAsPerLoginStatus`. The caller appends
 * `/:userName/:ambiguous/:sessionId/:tenantId?` segments.
 */
export function getBasePathAsPerLoginStatus(status?: string): string {
  switch (status) {
    case LoginErrorEnum.smsCodeRequired:
    case LoginErrorEnum.softwareTokenMfa:
      return '/confirmsms'
    case LoginErrorEnum.newPasswordRequired:
      return '/changepwd'
    case LoginErrorEnum.mfaSetup:
      return '/mfasetup'
    default:
      return '/login'
  }
}

export function loginErrorMessage(status?: string): string {
  switch (status) {
    case LoginErrorEnum.lockedOut:
      return 'Profile locked.'
    case LoginErrorEnum.invalidUsernamePassword:
      return 'Invalid credentials'
    case LoginErrorEnum.expiredSessionId:
    case LoginErrorEnum.loginAgain:
      return 'Session id expired, please go back to login.'
    case LoginErrorEnum.invalidSessionId:
      return 'Session id invalid, please go back to login.'
    case LoginErrorEnum.invalidCodeOrAuthState:
      return 'Invalid or expired code, please try again.'
    case LoginErrorEnum.newPasswordRequired:
      return ''
    case LoginErrorEnum.ambiguousUsername:
      return 'Ambiguous username — tenant id is required.'
    case LoginErrorEnum.nonAdminUser:
      return 'Only admins are allowed to sign in.'
    case LoginErrorEnum.userNotProvisioned:
      return 'Your account is not yet provisioned. Please contact your administrator.'
    case LoginErrorEnum.supplierPortalNotEnabled:
      return 'Supplier portal is not enabled for this tenant.'
    case LoginErrorEnum.supplierPortalAccessRestricted:
      return 'Supplier portal access is restricted for your account.'
    default:
      return 'Please check your input and try again.'
  }
}
